import type { RunMetricOutput } from "@/types/RunMetricOutput"

import {
  compareNumericalMetricValues,
  compareSetMetricValues,
} from "./metricValueComparison"

type TraceComparable = Partial<
  Pick<RunMetricOutput, "output_type" | "float_value" | "set_value">
>

export function compareTraceOutputs(
  a: TraceComparable,
  b: TraceComparable,
): boolean {
  const aSet = a.output_type === "set" || Array.isArray(a.set_value)
  const bSet = b.output_type === "set" || Array.isArray(b.set_value)
  if (aSet !== bSet) {
    return false
  }
  return aSet
    ? compareSetMetricValues(a, b)
    : compareNumericalMetricValues(a, b)
}

export function isTraceValuesOutput(mo: RunMetricOutput): boolean {
  return mo.output_type === "set" || Array.isArray(mo.set_value)
}

export function normalizeTraceOutput(output: RunMetricOutput): RunMetricOutput {
  // Scalar-only table cells encode arrays as JSON strings. Persisted Custom Trace
  // outputs are FLOAT or SET; only the table-cell badge needs this bridge.
  if (
    output.output_type !== "string" ||
    typeof output.string_value !== "string"
  ) {
    return output
  }
  try {
    const value: unknown = JSON.parse(output.string_value)
    return Array.isArray(value) &&
      value.every((item) => typeof item === "string")
      ? { ...output, output_type: "set", string_value: null, set_value: value }
      : output
  } catch {
    return output
  }
}

export function traceOutputGroups(outputs: RunMetricOutput[]) {
  const groups = new Map<string, RunMetricOutput[]>()
  for (const output of outputs) {
    const version = output.metric_version_id
      ? ["id", output.metric_version_id]
      : output.metric_version_number != null
        ? ["number", output.metric_version_number]
        : ["legacy"]
    const key = JSON.stringify([...version, isTraceValuesOutput(output)])
    const group = groups.get(key) ?? []
    group.push(output)
    groups.set(key, group)
  }
  return groups
}
